import { Component, Injector, OnInit } from '@angular/core';
import {
    IPersonExportDto,
    IPersonExportTableHeaders,
    IPersonOverviewRow,
    PersonBulkUpdate,
    PersonsFilter,
    PersonsProvider,
    TagsProvider,
} from '@modeso/aumico-lib-companies-fe';
import {
    BulkCreateFinancialYearJobStatusDto,
    BulkCreateFinancialYearPreviewDto,
    BulkCreateStatus,
} from '@modeso/aumico-lib-annualreport-fe';
import { IPageHeader, NotificationType } from '@modeso/aumico-lib-core-fe';
import { TasksUpdate } from '@modeso/aumico-lib-companies-fe/enums/tasks-update.enum';
import { OrganizationProvider } from '@modeso/aumico-lib-users-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/enums/cpmm-state.enum';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IPersonDTO, ITagDTO } from '@modeso/types__aumico-lib-companies-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subscription, filter, switchMap, takeWhile, timer } from 'rxjs';
import {
    PersonFinancialYearPreviewDto,
    PersonProjectsService,
} from '../../../../../../aumico-lib-projects-fe/src/lib';
import { Paths } from '../../../enums/paths.enum';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';

/**
 * REV-22155 — persons overview page: loads persons, tags and users, filters them and hands the
 * ready-made rows to the `aumico-persons-overview` pure view. Mirrors `CompaniesListPage` plus the
 * filtering that for companies lives inside the big list component.
 */
@UntilDestroy()
@Component({
    selector: 'app-persons-list-page',
    templateUrl: './persons-list.page.html',
    styleUrls: ['./persons-list.page.scss'],
})
export class PersonsListPage extends BasePageComponent implements OnInit {
    readonly pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.PERSONS_MAIN_NAVIGATION,
    };

    persons: IPersonDTO[] = [];
    tags: ITagDTO[] = [];
    users: INameId[] = [];
    filter: PersonsFilter = {};
    showInactive = false;

    rows: IPersonOverviewRow[] = [];
    activeCount = 0;
    cpmmActive = false;
    inactiveCount = 0;

    private organizationName = '';

    // REV-22155 — "Nächstes Geschäftsjahr": the company preview dialog, fed with person data,
    // then the progress banner over the list (copied from the companies overview).
    nextYearPreview: BulkCreateFinancialYearPreviewDto | null = null;
    nextYearJobStatus: BulkCreateFinancialYearJobStatusDto | null = null;
    nextYearProgress = 0;
    nextYearLoading = false;
    nextYearInProgress = false;
    private nextYearPolling: Subscription | null = null;

    constructor(
        injector: Injector,
        private readonly personsProvider: PersonsProvider,
        private readonly tagsProvider: TagsProvider,
        private readonly organizationProvider: OrganizationProvider,
        private readonly personProjectsService: PersonProjectsService,
    ) {
        super(injector);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.organizationProvider
            .getPossibleAssignees()
            .pipe(untilDestroyed(this))
            .subscribe((users) => {
                this.users = users.map((user) => ({ id: user.id, name: `${user.firstName} ${user.lastName}` }));
                this.rebuildRows();
            });

        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(untilDestroyed(this))
            .subscribe((organization) => {
                this.organizationName = organization?.name ?? '';
                // Sachbearbeiter change asks "which tasks" only with CPMM (like the companies overview).
                this.cpmmActive = organization?.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
            });

        this.personsProvider
            .getPersons$()
            .pipe(untilDestroyed(this))
            .subscribe((persons) => {
                this.persons = persons ?? [];
                this.rebuildRows();
            });

        this.tagsProvider
            .getTags$()
            .pipe(untilDestroyed(this))
            .subscribe((tags) => {
                this.tags = tags ?? [];
                this.rebuildRows();
            });

        // The remembered filter survives profile → back (the store lives as long as the tab).
        this.personsProvider
            .getPersonsFilter$()
            .pipe(untilDestroyed(this))
            .subscribe((filter) => {
                this.filter = filter ?? {};
                this.rebuildRows();
            });

        this.showErrors();
        this.personsProvider.dispatchPersonsList();
        this.tagsProvider.dispatchTagsList();
        this.organizationProvider.dispatchGetUsers();
        this.organizationProvider.dispatchGetCurrentOrganization();
    }


    /**
     * Failures of status changes, bulk actions and tag calls only landed in the store and stayed
     * silent. Shown once, then cleared so the same error is not reported again.
     */
    private showErrors(): void {
        this.personsProvider
            .getPersonsError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.fireErrorMessage();
                this.personsProvider.resetPersonsErrors();
            });

        this.tagsProvider
            .getTagsError$()
            .pipe(
                filter((error) => error != null),
                untilDestroyed(this),
            )
            .subscribe(() => {
                this.fireErrorMessage();
                this.tagsProvider.resetTagsErrors();
            });
    }

    // ── from the pure view ─────────────────────────────────────────────────────────────────────

    onTabChange(showInactive: boolean): void {
        this.showInactive = showInactive;
        this.rebuildRows();
    }

    onFilterChange(filter: PersonsFilter): void {
        this.personsProvider.dispatchSetPersonsFilter(filter);
    }

    onPersonClick(personId: string): void {
        this.router.navigate([this.personsRoot, personId, Paths.PROJECT]);
    }

    onNew(): void {
        this.router.navigate([this.personsRoot, Paths.ADD]);
    }

    onAssigneeChange(change: { personId: string; userId: string }): void {
        this.bulkUpdate([{ _id: change.personId, assignee: change.userId, assignedOn: new Date() }]);
    }

    onClerkChange(change: { personId: string; userId: string; tasksUpdate: TasksUpdate }): void {
        this.bulkUpdate([{ _id: change.personId, clerk: change.userId, assignedOnClerk: new Date() }], change.tasksUpdate);
    }

    onBulkAssigneeChange(change: { personIds: string[]; userId: string }): void {
        const assignedOn = new Date();
        this.bulkUpdate(change.personIds.map((id) => ({ _id: id, assignee: change.userId, assignedOn })));
    }

    onBulkClerkChange(change: { personIds: string[]; userId: string; tasksUpdate: TasksUpdate }): void {
        const assignedOnClerk = new Date();
        this.bulkUpdate(
            change.personIds.map((id) => ({ _id: id, clerk: change.userId, assignedOnClerk })),
            change.tasksUpdate,
        );
    }

    onBulkDeactivate(personIds: string[]): void {
        this.bulkUpdate(personIds.map((id) => ({ _id: id, active: false })));
    }

    /** Button exists; wired up with the person projects (context-Projekte.md). */
    /** Preview first: per person the new period, or why it is skipped (inactive, no project yet). */
    onBulkCreateNextFinancialYear(personIds: string[]): void {
        this.nextYearLoading = true;
        this.personProjectsService.previewNextFinancialYear(personIds).subscribe({
            next: (preview) => {
                this.nextYearLoading = false;
                this.nextYearPreview = this.toCompanyPreview(preview);
            },
            error: () => {
                this.nextYearLoading = false;
                this.aumicoCoreProvider.dispatchAddNotification(
                    'aumico_companies_bulk_create_preview_failed',
                    'aumico_companies_bulk_create_preview_failed',
                    NotificationType.ERROR,
                );
            },
        });
    }

    /** Only the ready persons; the dialog closes, the job runs in the background with a progress banner (like companies). */
    onNextYearConfirm(): void {
        const personIds = (this.nextYearPreview?.previews ?? [])
            .filter((p) => p.status === BulkCreateStatus.READY)
            .map((p) => p.companyId);
        if (personIds.length === 0) {
            return;
        }
        this.nextYearLoading = true;
        this.nextYearPreview = null;
        this.personProjectsService.startNextFinancialYear(personIds).subscribe({
            next: ({ jobId }) => {
                this.nextYearInProgress = true;
                this.pollNextYearJob(jobId);
            },
            error: () => this.finishNextYear('aumico_companies_bulk_create_failed', NotificationType.ERROR),
        });
    }

    onNextYearCancel(): void {
        this.stopNextYearPolling();
        this.resetNextYear();
    }

    private pollNextYearJob(jobId: string): void {
        this.stopNextYearPolling();
        this.nextYearPolling = timer(0, 1500)
            .pipe(
                switchMap(() => this.personProjectsService.getNextFinancialYearJobStatus(jobId)),
                takeWhile((status) => status.status === 'pending' || status.status === 'processing', true),
                untilDestroyed(this),
            )
            .subscribe({
                next: (status) => {
                    this.nextYearJobStatus = status;
                    this.nextYearProgress =
                        status.totalCompanies > 0 ? Math.round((status.processedCompanies / status.totalCompanies) * 100) : 0;
                    if (status.status === 'completed') {
                        const message = this.i18n.t('aumico_companies_bulk_create_success', {
                            created: status.successCount,
                            failed: status.failureCount,
                        }) as string;
                        this.finishNextYear(message, status.successCount > 0 ? NotificationType.SUCCESS : NotificationType.ERROR);
                    } else if (status.status === 'failed') {
                        this.finishNextYear('aumico_companies_bulk_create_job_failed', NotificationType.ERROR);
                    }
                },
                error: () => this.finishNextYear('aumico_companies_bulk_create_job_failed', NotificationType.ERROR),
            });
    }

    private finishNextYear(message: string, type: NotificationType): void {
        this.stopNextYearPolling();
        this.resetNextYear();
        this.aumicoCoreProvider.dispatchAddNotification(message, message, type, 4000);
    }

    private stopNextYearPolling(): void {
        this.nextYearPolling?.unsubscribe();
        this.nextYearPolling = null;
    }

    private resetNextYear(): void {
        this.nextYearPreview = null;
        this.nextYearJobStatus = null;
        this.nextYearProgress = 0;
        this.nextYearLoading = false;
        this.nextYearInProgress = false;
    }

    /** The company dialog shows name, new period, year group and reason — the person data in its shape. */
    private toCompanyPreview(preview: PersonFinancialYearPreviewDto): BulkCreateFinancialYearPreviewDto {
        const reasonKey = {
            INACTIVE: 'aumico_person_projects_next_year_reason_inactive',
            NO_PROJECT: 'aumico_person_projects_next_year_reason_no_project',
            NOT_FOUND: 'aumico_person_projects_next_year_reason_not_found',
        } as const;
        return {
            previews: preview.previews.map((p) => ({
                companyId: p.personId,
                companyName: p.personName,
                status: p.status === 'READY' ? BulkCreateStatus.READY : BulkCreateStatus.SKIP,
                ...(p.reason != null && { reason: this.i18n.t(reasonKey[p.reason]) as string }),
                ...(p.periodStart != null && { nextProjectStartDate: p.periodStart }),
                ...(p.periodEnd != null && { nextProjectEndDate: p.periodEnd, targetYear: p.periodEnd.substring(0, 4) }),
            })),
            readyCount: preview.readyCount,
            attentionCount: 0,
            skipCount: preview.skipCount,
        };
    }

    /** The page builds rows and headers; aumico-be only fills the template (§4). */
    onExport(): void {
        const header = (key: string): string => this.i18n.t(key).toString();
        const tableHeaders: IPersonExportTableHeaders = {
            firstName: header('aumico_persons_form_first_name'),
            lastName: header('aumico_persons_form_last_name'),
            street: header('aumico_persons_form_street'),
            additionToAddress: header('aumico_persons_form_addition_to_address'),
            zip: header('aumico_persons_form_zip'),
            city: header('aumico_persons_form_city'),
            email: header('aumico_persons_form_email'),
            phone: header('aumico_persons_form_phone'),
            assignee: header('aumico_persons_form_assignee'),
            clerk: header('aumico_persons_form_clerk'),
            tags: header('aumico_tags_box_title'),
            companies: header('aumico_persons_form_section_companies'),
        };

        // Every person of the organization, not only the filtered ones (§4).
        const persons: IPersonExportDto[] = this.persons.map((person) => ({
            firstName: person.firstName ?? '',
            lastName: person.lastName ?? '',
            street: person.street ?? '',
            additionToAddress: person.additionToAddress ?? '',
            zip: person.zip ?? '',
            city: person.city ?? '',
            email: this.contactValues(person, 'EMAIL'),
            phone: this.contactValues(person, 'PHONE'),
            assignee: this.userName(person.assignee),
            clerk: this.userName(person.clerk),
            tags: this.tagNames(person).join(', '),
            // "Firmenname (Rolle)", deleted companies included without a mark (§4).
            companies: (person.companies ?? [])
                .map((link) => `${link.companyName} (${this.roleLabel(link.role)})`)
                .join(', '),
        }));

        const now = new Date();
        const date = `${String(now.getDate()).padStart(2, '0')}_${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}`;
        const fileName = `${header('aumico_reports_export_all_persons_file_name')}_${this.organizationName}_${date}`;
        this.personsProvider.dispatchExportPersonsDataAsExcel(tableHeaders, persons, fileName);
    }

    // ── helpers ────────────────────────────────────────────────────────────────────────────────

    private get personsRoot(): string {
        return `/${this.localStorageService.getCurrentLanguage()}/${Paths.HOME}/${Paths.PERSONS}`;
    }

    private bulkUpdate(changes: PersonBulkUpdate[], tasksUpdate?: TasksUpdate): void {
        if (changes.length > 0) {
            this.personsProvider.dispatchBulkUpdatePersons(changes, tasksUpdate);
        }
    }

    /** Tab, search and the three filters — the pure view only displays what comes out of here. */
    private rebuildRows(): void {
        const search = (this.filter.textSearch ?? '').trim().toLocaleLowerCase();
        const assignees = this.filter.mandateManagerIds ?? [];
        const clerks = this.filter.clerkIds ?? [];
        const tags = this.filter.tagIds ?? [];

        this.activeCount = this.persons.filter((person) => person.active).length;
        this.inactiveCount = this.persons.length - this.activeCount;

        this.rows = this.persons
            .filter((person) => person.active !== this.showInactive)
            .filter((person) => assignees.length === 0 || assignees.includes(person.assignee))
            .filter((person) => clerks.length === 0 || clerks.includes(person.clerk))
            .filter((person) => tags.length === 0 || (person.tags ?? []).some((tag) => tags.includes(tag)))
            .filter((person) => search === '' || this.matchesSearch(person, search))
            .map((person) => ({
                _id: person._id,
                // "Nachname Vorname" (§9).
                name: `${person.lastName} ${person.firstName}`.trim(),
                street: person.street ?? '',
                zip: person.zip ?? '',
                city: person.city ?? '',
                tagNames: this.tagNames(person),
                assigneeId: person.assignee ?? null,
                assigneeName: this.userName(person.assignee),
                clerkId: person.clerk ?? null,
                clerkName: this.userName(person.clerk),
            }));
    }

    private matchesSearch(person: IPersonDTO, search: string): boolean {
        const haystack = [
            // Full name in both orders, so "Anzeige Test" and "Test Anzeige" are found.
            `${person.lastName} ${person.firstName}`,
            `${person.firstName} ${person.lastName}`,
            person.street,
            person.zip,
            person.city,
            ...this.tagNames(person),
        ];
        return haystack.some((value) => (value ?? '').toLocaleLowerCase().includes(search));
    }

    /** Tags of the person, resolved to names; an id that no longer exists is dropped. */
    private tagNames(person: IPersonDTO): string[] {
        return (person.tags ?? [])
            .map((tagId) => this.tags.find((tag) => tag._id === tagId)?.name)
            .filter((name): name is string => name != null);
    }

    private userName(userId: string | undefined): string {
        return this.users.find((user) => user.id === userId)?.name ?? '';
    }

    private contactValues(person: IPersonDTO, type: 'EMAIL' | 'PHONE'): string {
        return (person.contacts ?? [])
            .filter((contact) => contact.type === type)
            .map((contact) => contact.value)
            .join(', ');
    }

    private roleLabel(role: string): string {
        return this.i18n.t(`aumico_persons_form_role_${role}`).toString();
    }
}
